// Mirrors trpc.workday outputs (apps/web/src/api/trpc/routers/workday.ts).

export const DEPARTMENTS = ["kitchen", "front_of_house", "bar", "other"] as const;
export type Department = (typeof DEPARTMENTS)[number];

export type StepKind = "check" | "yes_no" | "temperature" | "number" | "text";
export const STEP_KINDS: StepKind[] = ["check", "yes_no", "temperature", "number", "text"];

export type Step = {
  id?: number;
  title: string;
  kind: StepKind;
  required: boolean;
  min_value: number | null;
  max_value: number | null;
  unit: string | null;
};

export type Procedure = {
  id: number;
  name: string;
  description: string | null;
  department: Department;
  schedule_days: number[];
  schedule_time: string | null;
  active: boolean;
  steps: (Step & { id: number })[];
};

export type ProcedureSummary = Omit<Procedure, "steps"> & {
  step_count: number;
  last_run: { run_date: string; status: string; issues: number; completed_at: string | null } | null;
};

export type Answer = {
  checked: boolean | null;
  value_number: number | null;
  value_text: string | null;
  is_issue: boolean;
  answered_at: string;
  answered_by_name: string | null;
};

export type Run = {
  id: number;
  procedure_id: number;
  procedure_name: string;
  department: string;
  run_date: string;
  status: "in_progress" | "completed";
  issues: number;
  notes: string | null;
  started_at: string;
  completed_at: string | null;
  completed_by_name: string | null;
  steps: (Step & { id: number; answer: Answer | null })[];
};

export type PrepItem = { id?: number; name: string; par_qty: number; unit: string | null; notes: string | null };

export type PrepList = {
  id: number;
  name: string;
  department: Department;
  schedule_days: number[];
  active: boolean;
  items: (PrepItem & { id: number })[];
};

export type PrepListSummary = Omit<PrepList, "items"> & { item_count: number; item_names: string[] | null };

export type PrepRun = {
  id: number;
  prep_list_id: number;
  prep_list_name: string;
  department: string;
  run_date: string;
  completed_at: string | null;
  items: {
    id: number;
    prep_item_id: number;
    name: string;
    unit: string | null;
    notes: string | null;
    par_qty: number;
    on_hand: number | null;
    to_prep: number;
    done: boolean;
    done_at: string | null;
    done_by_name: string | null;
  }[];
};

export type Task = {
  id: number;
  title: string;
  notes: string | null;
  department: string | null;
  assigned_to: number | null;
  assigned_to_name?: string | null;
  due_date: string | null;
  priority: "normal" | "high";
  status: "open" | "done";
  done_at: string | null;
  overdue?: boolean;
};

export type MyDay = {
  date: string;
  procedures: {
    id: number;
    name: string;
    department: string;
    schedule_time: string | null;
    step_count: number;
    run_id: number | null;
    status: string | null;
    issues: number | null;
    completed_at: string | null;
    answered: number;
  }[];
  prep_lists: {
    id: number;
    name: string;
    department: string;
    item_count: number;
    run_id: number | null;
    completed_at: string | null;
    done_count: number;
  }[];
  tasks: Task[];
};

export type StaffMember = { id: number; name: string };
